import axios from "axios";

// The API is served by Cloudflare Pages Functions from the same origin as the
// site, so there is no base URL to configure and no cross-origin credentials
// to negotiate — the session cookie is first-party.
export const api = axios.create({ baseURL: "/api" });

// Public
export const getConfig = () => api.get("/config").then((r) => r.data);
export const getSite = () => api.get("/site").then((r) => r.data);
export const getCaseStudies = () =>
    api.get("/case-studies").then((r) => r.data);
export const getCaseStudy = (id) =>
    api.get(`/case-studies/${id}`).then((r) => r.data);
export const getCareers = () => api.get("/careers").then((r) => r.data);
export const getLegal = (page) =>
    api.get(`/legal/${page}`).then((r) => r.data);

// An invoice is read with its token, which is the whole of its security — it is
// never listed, never linked to, and never indexed.
export const getInvoice = (token) =>
    api.get(`/invoice/${token}`).then((r) => r.data);

// Auth
export const authMe = () => api.get("/auth/me").then((r) => r.data);
export const googleSignIn = (credential) =>
    api.post("/auth/google", { credential }).then((r) => r.data);
export const logout = () => api.post("/auth/logout").then((r) => r.data);

// Admin — case studies
export const adminListCaseStudies = () =>
    api.get("/admin/case-studies").then((r) => r.data);
export const adminCreateCaseStudy = (payload) =>
    api.post("/admin/case-studies", payload).then((r) => r.data);
export const adminUpdateCaseStudy = (id, payload) =>
    api.put(`/admin/case-studies/${id}`, payload).then((r) => r.data);
export const adminDeleteCaseStudy = (id) =>
    api.delete(`/admin/case-studies/${id}`).then((r) => r.data);

// Admin — careers
export const adminListCareers = () =>
    api.get("/admin/careers").then((r) => r.data);
export const adminCreateCareer = (payload) =>
    api.post("/admin/careers", payload).then((r) => r.data);
export const adminUpdateCareer = (id, payload) =>
    api.put(`/admin/careers/${id}`, payload).then((r) => r.data);
export const adminDeleteCareer = (id) =>
    api.delete(`/admin/careers/${id}`).then((r) => r.data);

// Admin — invoices. Editing is only accepted while an invoice is a draft; once
// issued it changes state through `adminInvoiceAction` and nothing else.
export const adminListInvoices = () =>
    api.get("/admin/invoices").then((r) => r.data);
export const adminCreateInvoice = (payload) =>
    api.post("/admin/invoices", payload).then((r) => r.data);
export const adminUpdateInvoice = (id, payload) =>
    api.put(`/admin/invoices/${id}`, payload).then((r) => r.data);
export const adminDeleteInvoice = (id) =>
    api.delete(`/admin/invoices/${id}`).then((r) => r.data);
export const adminInvoiceAction = (id, payload) =>
    api.post(`/admin/invoices/${id}`, payload).then((r) => r.data);

export const adminGetInvoiceSettings = () =>
    api.get("/admin/invoice-settings").then((r) => r.data);
export const adminUpdateInvoiceSettings = (payload) =>
    api.put("/admin/invoice-settings", payload).then((r) => r.data);

// Admin — copy
export const adminUpdateSite = (payload) =>
    api.put("/admin/site", payload).then((r) => r.data);
export const adminUpdateLegal = (page, payload) =>
    api.put(`/admin/legal/${page}`, payload).then((r) => r.data);

/**
 * Uploads an image to R2 through the admin API. The file is stored exactly as
 * provided — no resizing or re-encoding — and served back from /media/<key>.
 * Returns { url, key }.
 */
export async function uploadImage(file, folder = "work") {
    const form = new FormData();
    form.append("file", file);
    form.append("folder", folder);
    const { data } = await api.post("/admin/uploads", form);
    return data;
}

// Note: dropping an image from a case study does not delete it from R2. That
// is deliberate — the editor can be cancelled after a removal, and an orphaned
// file is a far better outcome than a live case study pointing at a deleted
// one. Deleting a whole case study does clean up its objects. The admin
// DELETE /api/admin/uploads endpoint exists for manual tidying.
